/**
 * Tell every window of this browser which user is signed in now (#871).
 *
 * The session cookie is **browser-wide by design** and must stay that way: the
 * platform opens real windows of the same origin — the detached video window and
 * the detached sEEG/neuroimage viewers — which inherit the session. Scoping
 * sessions per tab would break them.
 *
 * What that costs is this: when someone signs in as a different user in another
 * tab, every tab and window already open keeps rendering the previous user's
 * interface. Since #865 a request from such a page is refused (`409
 * session_user_mismatch`), so nothing executes under the wrong identity — but the
 * user only finds out when they press something.
 *
 * A login is a form POST followed by a redirect, so there is no client-side "at
 * login" moment to hook. The page rendered AFTER the login is that moment: it
 * announces the identity it was rendered for, and any other window whose own
 * identity differs marks itself stale. Announcing "nobody" covers logout for
 * free, which today leaves the other tabs showing no sign that the session ended.
 *
 * The detached windows need no participant of their own: they run no code at all.
 * Their DOM is portalled out of the main window's Preact tree
 * (`components/popout/popout_frame.tsx`), so reacting here is reacting there —
 * what they need is for the notice to be rendered INTO their document, which
 * `PopoutFrame` does.
 *
 * This is a warning, not a guard. The `409` refusal remains the thing that
 * actually prevents an action under an identity the user did not choose.
 */

import { BroadcastPort, openBroadcastPort, setBroadcastFactoryForTests } from '@brainfocus/web/shared/platform/broadcast';

/**
 * The slice of `BroadcastChannel` used here, so tests can supply a synchronous
 * double; opened through the shared factory (`shared/platform/broadcast.ts`).
 */
export type IdentityChannel = BroadcastPort;

/** Announcement payload. `userId` is `null` when nobody is signed in. */
interface IdentityAnnouncement {
    userId: number | null;
}

/**
 * Why this window can no longer be operated. The two cases need different words:
 * after a sign-out there is no "other account" to name.
 *
 *  - `other-user`: somebody else signed in and now owns the session.
 *  - `signed-out`: the session ended — a sign-out in another tab.
 */
export type StaleReason = 'other-user' | 'signed-out';

const CHANNEL_NAME = 'bf-session-identity';

let channel: IdentityChannel | null = null;
let announced = false;
let stale: StaleReason | null = null;
const listeners = new Set<() => void>();

/**
 * Identity this page was rendered for, or `null` when nobody is signed in.
 * Injected by `app/templates/layout/body.html`; read defensively because tests
 * and the pre-login pages do not define it.
 */
function pageUserId(): number | null {
    const user = typeof BF_SESSION_USER === 'undefined' ? null : BF_SESSION_USER;
    return user ?? null;
}

function isAnnouncement(data: unknown): data is IdentityAnnouncement {
    if (typeof data !== 'object' || data === null) {
        return false;
    }
    const value = (data as { userId?: unknown }).userId;
    return value === null || typeof value === 'number';
}

function markStale(reason: StaleReason): void {
    if (stale) {
        // Already unusable. Keeping the first reason avoids re-labelling the notice
        // under the user mid-read, and the recovery is the same either way.
        return;
    }
    stale = reason;
    // Copy first: a listener may unsubscribe itself while being notified.
    [...listeners].forEach(listener => listener());
}

function receive(data: unknown): void {
    if (!isAnnouncement(data)) {
        return;
    }
    const own = pageUserId();
    if (own === null) {
        // A page rendered for nobody (the login page) has no identity to be stale;
        // blocking it with the notice would trap the user out of signing in.
        return;
    }
    if (data.userId === null) {
        // Nobody is signed in: the session ended rather than changing hands. No
        // extra field on the wire says so — `userId: null` already does.
        markStale('signed-out');
    } else if (data.userId !== own) {
        markStale('other-user');
    }
}

/**
 * Announce this page's identity, once per page load, and start listening for
 * other windows' announcements.
 *
 * Called at bootstrap. The channel never delivers a message to its own sender, so
 * announcing cannot mark this page stale.
 */
export function announceSessionIdentity(): void {
    if (announced) {
        return;
    }
    announced = true;
    channel = openBroadcastPort(CHANNEL_NAME);
    if (!channel) {
        return;
    }
    channel.onMessage = receive;
    channel.postMessage({ userId: pageUserId() } satisfies IdentityAnnouncement);
}

/** Whether this window can still be operated as the user it was built for. */
export function isSessionStale(): boolean {
    return stale !== null;
}

/** Why the window is stale, or `null` while the session is still current. */
export function staleReason(): StaleReason | null {
    return stale;
}

/**
 * Subscribe to the transition to stale. Returns the unsubscribe function.
 *
 * Fires at most once — the page cannot become un-stale, only reloaded. A
 * subscriber that arrives after the fact is not notified retroactively; it reads
 * {@link isSessionStale} for the current state, which is what `useStaleSession`
 * does.
 */
export function onSessionStale(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

/**
 * Reset every module-level value. Test seam only — never call from app code.
 */
export function resetSessionIdentityForTests(): void {
    channel?.close();
    channel = null;
    announced = false;
    stale = null;
    listeners.clear();
}

/**
 * Install `factory` as the shared broadcast factory for this channel (every other
 * channel gets none). Test seam only — never call from app code.
 *
 * The real `BroadcastChannel` is deliberately never exercised by the suite: under
 * jsdom the bare global is Node's `worker_threads` implementation, which keeps the
 * event loop alive until every channel is closed and would hang Mocha.
 */
export function setChannelFactoryForTests(factory: () => IdentityChannel | null): void {
    setBroadcastFactoryForTests(name => (name === CHANNEL_NAME ? factory() : null));
}
